import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #loadMoreObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.addEventListener('click', this.#handleLoadMoreClick);
    this.#updateLoadMore();
    this.#observeLoadMoreChanges();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('click', this.#handleLoadMoreClick);
    this.#loadMoreObserver?.disconnect();
  }

  #handleLoadMoreClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-nobo-load-more]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    button.disabled = true;
    await this.renderNextPage();
    button.disabled = false;
    this.#updateLoadMore();
  };

  #updateLoadMore() {
    const { grid } = this.refs;
    const wrap = this.querySelector('[data-nobo-load-more-wrap]');
    const count = this.querySelector('[data-nobo-load-more-count]');
    const bar = this.querySelector('[data-nobo-load-more-bar]');
    const button = this.querySelector('[data-nobo-load-more]');

    if (!grid || !wrap) return;

    const cards = Array.from(grid.querySelectorAll(':scope > [data-product-id]'));
    const totalProducts = Number(grid.dataset.totalProducts || cards.length);
    const loadedProducts = cards.length;
    const lastCard = cards[cards.length - 1];
    const currentPage = Number(lastCard?.dataset.page || 1);
    const lastPage = Number(grid.dataset.lastPage || 1);
    const hasNextPage = currentPage < lastPage;

    if (count) {
      const countTemplate = count.dataset.noboLoadMoreCountTemplate;
      count.textContent = countTemplate
        ? countTemplate.replace('__LOADED__', String(loadedProducts)).replace('__TOTAL__', String(totalProducts))
        : `${loadedProducts} / ${totalProducts}`;
    }
    if (bar instanceof HTMLElement) {
      bar.style.width = `${Math.min(100, (loadedProducts / totalProducts) * 100)}%`;
    }
    if (button instanceof HTMLButtonElement) button.hidden = !hasNextPage;
    if (wrap instanceof HTMLElement) wrap.hidden = !hasNextPage && loadedProducts >= totalProducts;
  }

  #observeLoadMoreChanges() {
    const { grid } = this.refs;
    if (!grid) return;

    this.#loadMoreObserver?.disconnect();
    this.#loadMoreObserver = new MutationObserver(() => queueMicrotask(() => this.#updateLoadMore()));
    this.#loadMoreObserver.observe(grid, { childList: true });
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
